import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import Yup from 'yup';
import SubmitButton from 'base/components/forms/SubmitButton';
import { FormikInputField, FormikSelectField, Legend, Fieldset, Label } from 'base/components/forms';
import Columns from 'base/components/Columns';
import Col from 'base/components/Col';
import Button from 'base/components/Button';
import FormikCheckboxField from 'base/components/forms/formik/FormikCheckboxField';
import {
  getEditingUser,
  getEditingUserIsLoading,
  getUsersById,
  getUsersSelectOptions,
} from 'auth/store/users/selectors';
import { getRoleSelectOptions } from 'auth/store/roles/selectors';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import FormikDropzoneField from 'base/components/forms/formik/FormikDropzoneField';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import ProfilePicture from 'base/components/ProfilePicture';
import { fetchAllDefaultDashboards, getDefaultDashboardsSelectOptions } from '../../../dashboard';

export default function ContactForm({ contact, isNewUser, isAdminForm, onSubmit }) {
  const dispatch = useAppDispatch();
  const editingUser = useAppSelector(getEditingUser);
  const user = isNewUser ? {} : editingUser;
  const userIsLoading = useAppSelector(getEditingUserIsLoading);
  const roleSelectOptions = useAppSelector(getRoleSelectOptions);
  const userSelectOptions = useAppSelector(getUsersSelectOptions);
  const usersById = useAppSelector(getUsersById);
  const defaultDashboardsSelectOptions = useAppSelector(getDefaultDashboardsSelectOptions);

  const [hasUser, setHasUser] = useState(contact?.user || isNewUser || false);
  const [clonePermissionsIsActive, setClonePermissionsIsActive] = useState(false);

  useEffect(() => {
    if (isAdminForm) {
      dispatch(fetchAllDefaultDashboards());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showClonePermissions = () => setClonePermissionsIsActive(true);

  const createUser = () => setHasUser(true);

  const initialValues = {
    isPublished: contact.isPublished,
    salutation: contact.salutation || '',
    firstName: contact.firstName || '',
    lastName: contact.lastName || '',
    email: contact.email || '',
    phone: contact.phone || '',
    mobile: contact.mobile || '',
    fax: contact.fax || '',
    switchboard: contact.switchboard || '',
    company: contact.company || '',
    street: contact.street || '',
    location: contact.location || '',
    country: contact.country || '',
    position: contact.position || '',
    supervisor: contact.supervisor || '',
    division: contact.division || '',
    imageFile: [],
  };
  if (isAdminForm && (user || isNewUser)) {
    initialValues.user = {
      roles: user && user.roles ? user.roles : [],
      isActive: user && user.isActive ? user.isActive : false,
      isUsing2FA: user && user.isUsing2FA ? user.isUsing2FA : isNewUser,
      defaultDashboard: user && user.defaultDashboard ? user.defaultDashboard.id : '',
      denyContactList: user && user.denyContactList ? user.denyContactList : false,
    };
  }
  if (isAdminForm || isNewUser) {
    initialValues.isPublished = true;
  }

  const personalFields = (
    <Fieldset>
      <Legend>Personal</Legend>
      <Columns>
        <Col>
          <Field
            type="select"
            name="salutation"
            label="Salutation"
            component={FormikSelectField}
            options={[
              { value: 'Ms.', label: 'Miss' },
              { value: 'Mr.', label: 'Mister' },
            ]}
          />
        </Col>
        <Col>
          <Field type="text" name="email" label="Email" component={FormikInputField} />
        </Col>
      </Columns>
      <Columns>
        <Col>
          <Field type="text" name="firstName" label="First name" component={FormikInputField} />
        </Col>
        <Col>
          <Field type="text" name="lastName" label="Last name" component={FormikInputField} />
        </Col>
      </Columns>
      <Columns>
        <Col>
          <Field
            type="text"
            name="imageFile"
            label="New image"
            multiple={false}
            component={FormikDropzoneField}
            accept="image/*"
          />
        </Col>
        <Col>
          <FieldWrapper>
            <Label>Current image</Label>
            {contact?.image ? (
              <ProfilePicture contactId={contact?.id} mediaId={contact?.image?.id} size="large" />
            ) : (
              'No image'
            )}
          </FieldWrapper>
        </Col>
      </Columns>
    </Fieldset>
  );

  const companyFields = (
    <Fieldset isNested>
      <Legend>Company</Legend>
      <Columns>
        <Col>
          <Field type="text" name="position" label="Function" component={FormikInputField} />
        </Col>
        <Col>
          <Field type="text" name="supervisor" label="Supervisor" component={FormikInputField} />
        </Col>
      </Columns>
      <Field
        type="text"
        name="division"
        label="Division"
        component={FormikSelectField}
        options={[
          { value: 'Allvisa AG', label: 'Allvisa AG' },
          { value: 'Bucher Emhart Glass', label: 'Bucher Emhart Glass' },
          { value: 'Bucher Emhart Glass SA', label: 'Bucher Emhart Glass SA' },
          { value: 'Bucher Hydraulics', label: 'Bucher Hydraulics' },
          { value: 'Bucher Industries', label: 'Bucher Industries' },
          { value: 'Bucher Landtechnik', label: 'Bucher Landtechnik' },
          { value: 'Bucher Management', label: 'Bucher Management' },
          { value: 'Bucher Municipal', label: 'Bucher Municipal' },
          { value: 'Bucher Specials', label: 'Bucher Specials' },
          { value: 'Bucher Unipektin', label: 'Bucher Unipektin' },
          { value: 'Bucher-Guyer AG', label: 'Bucher-Guyer AG' },
          { value: 'Corporate Center', label: 'Corporate Center' },
          { value: 'Ernst&Young', label: 'Ernst&Young' },
          { value: 'Kuhn Group', label: 'Kuhn Group' },
          { value: 'n.a.', label: 'n.a.' },
          { value: 'NonOp', label: 'NonOp' },
          { value: 'NonOp (BBG)', label: 'NonOp (BBG)' },
          { value: 'NonOp (BFI)', label: 'NonOp (BFI)' },
          { value: 'NonOp (BIFR)', label: 'NonOp (BIFR)' },
          { value: 'NonOp (BISP)', label: 'NonOp (BISP)' },
          { value: 'NonOp (BIUS)', label: 'NonOp (BIUS)' },
        ]}
      />
    </Fieldset>
  );

  const addressFields = (
    <Fieldset isNested>
      <Legend>Address</Legend>
      <Columns>
        <Col>
          <Field type="text" name="company" label="Company" component={FormikInputField} />
        </Col>
        <Col>
          <Field type="text" name="street" label="Street" component={FormikInputField} />
        </Col>
      </Columns>
      <Columns>
        <Col>
          <Field type="text" name="location" label="Location" component={FormikInputField} />
        </Col>
        <Col>
          <Field type="text" name="country" label="Country" component={FormikInputField} />
        </Col>
      </Columns>
    </Fieldset>
  );

  const phoneFields = (
    <Fieldset isNested>
      <Legend>Phone</Legend>
      <Columns>
        <Col>
          <Field type="text" name="phone" label="Phone" component={FormikInputField} />
        </Col>
        <Col>
          <Field type="text" name="mobile" label="Mobile" component={FormikInputField} />
        </Col>
      </Columns>
      <Columns>
        <Col>
          <Field type="text" name="fax" label="Fax" component={FormikInputField} />
        </Col>
        <Col>
          <Field type="text" name="switchboard" label="Switchboard" component={FormikInputField} />
        </Col>
      </Columns>
    </Fieldset>
  );

  const userFields = (
    <>
      <Fieldset isNested>
        <Legend>Security</Legend>
        <Field type="text" name="user.isActive" component={FormikCheckboxField} label="Active" />
        <Field type="text" name="user.isUsing2FA" component={FormikCheckboxField} label="2-factor authentication" />
        <Field
          type="text"
          name="user.denyContactList"
          component={FormikCheckboxField}
          label="Deny access to contact list"
        />
        <Field
          type="select"
          name="user.roles"
          component={FormikSelectField}
          placeholder="Roles"
          label="Roles"
          isMulti
          options={roleSelectOptions}
        />
        {clonePermissionsIsActive ? (
          <Field
            type="select"
            name="user.clonePermissionsFrom"
            component={FormikSelectField}
            placeholder="Clone permissions from user"
            label="Clone permissions from user"
            options={userSelectOptions}
            onChange={({ form, selected }) => {
              // copy roles from selected users
              const otherUser = usersById[selected.value];
              form.setFieldValue('user.roles', otherUser.roles, true);
            }}
          />
        ) : (
          <Button
            variant="secondary"
            onClick={showClonePermissions}
            style={{ display: 'inline-block', marginBottom: '30px' }}
          >
            Clone permissions from another user
          </Button>
        )}
      </Fieldset>
      <Fieldset isNested>
        <Legend>Dashboard</Legend>
        {user && user.dashboard && (
          <FieldWrapper>This user has a custom dashboard and will thus not see the default dashboard</FieldWrapper>
        )}
        <Field
          type="select"
          name="user.defaultDashboard"
          component={FormikSelectField}
          placeholder=""
          label="Default dashboard"
          isClearable
          options={defaultDashboardsSelectOptions}
        />
      </Fieldset>
    </>
  );

  const form = (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        salutation: Yup.string().oneOf(['Ms.', 'Mr.']).required('Salutation is required'),
        firstName: Yup.string().required('First name is required.'),
        lastName: Yup.string().required('Last name is required.'),
        email: Yup.string().required('Email is required.').email('Please enter a valid email address.'),
        imageFile: Yup.array(),
        phone: Yup.string(),
        mobile: Yup.string(),
        fax: Yup.string(),
        switchboard: Yup.string(),
        company: Yup.string(),
        street: Yup.string(),
        location: Yup.string(),
        country: Yup.string(),
        position: Yup.string(),
        supervisor: Yup.string(),
        division: Yup.string(),
      })}
      render={(
        { errors, isSubmitting }, // eslint-disable-line
      ) => (
        <Form>
          {personalFields}

          {isAdminForm && (
            <Fieldset>
              <Legend>Intranet user</Legend>
              {user || hasUser ? (
                userFields
              ) : (
                <>
                  <div>No intranet user linked</div>
                  <Button variant="secondary" onClick={createUser} style={{ display: 'inline-block' }}>
                    Create user
                  </Button>
                </>
              )}
            </Fieldset>
          )}

          <Fieldset>
            <Legend>Contact details</Legend>
            <Field type="text" name="isPublished" component={FormikCheckboxField} label="Publish contact info" />
            {phoneFields}
            {addressFields}
            {companyFields}
          </Fieldset>

          <SubmitButton type="submit" disabled={isSubmitting}>
            Submit
          </SubmitButton>
        </Form>
      )}
      onSubmit={values => {
        const newContact = {
          salutation: values.salutation,
          lastName: values.lastName,
          firstName: values.firstName,
          email: values.email,
          phone: values.phone,
          mobile: values.mobile,
          fax: values.fax,
          switchboard: values.switchboard,
          company: values.company,
          street: values.street,
          location: values.location,
          country: values.country,
          position: values.position,
          supervisor: values.supervisor,
          division: values.division,
          imageFile: values.imageFile,
          isPublished: values.isPublished,
        };
        if (isAdminForm && values.user) {
          newContact.user = {
            ...user,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            isActive: values.user.isActive,
            isUsing2FA: values.user.isUsing2FA,
            roles: values.user.roles,
            dashboard: user && user.dashboard ? user.dashboard.id : undefined,
            defaultDashboard: values.user.defaultDashboard ? values.user.defaultDashboard : undefined,
            denyContactList: values.user.denyContactList,
          };
        }
        onSubmit(newContact);
      }}
    />
  );

  return <>{userIsLoading ? 'Loading' : form}</>;
}
ContactForm.propTypes = {
  contact: PropTypes.objectOf(PropTypes.any),
  onSubmit: PropTypes.func.isRequired,
  isAdminForm: PropTypes.bool,
  isNewUser: PropTypes.bool,
};
ContactForm.defaultProps = {
  contact: {},
  isAdminForm: false,
  isNewUser: false,
};
